import React from 'react';
import { Box, Typography } from '@mui/material';
import { People as PlayersIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { formatJerseyNumber } from '../../utils/formatUtils';

const TeamRoster = ({ 
  team, 
  variant: _variant = 'private', // 'private' or 'public'
  maxPlayers = null,
  showHeader = true,
  sx = {}
}) => {
  const { isAuthenticated } = useAuth();
  // Handle different data structures
  const players = team.players || team.roster || team.Roster || [];
  const _playerCount = team.playerCount || players.length;

  // Filter players - all players including managers will be shown together
  const allPlayers = players || [];
  
  // Position sorting order - including both full names and common abbreviations
  const positionOrder = [
    'GOALKEEPER', 'GK', 'GOALIE',
    'DEFENDER', 'DEFENCE', 'CB', 'LB', 'RB', 'LWB', 'RWB', 'FB',
    'MIDFIELDER', 'MIDFIELD', 'CDM', 'CM', 'CAM', 'LM', 'RM', 'MF',
    'FORWARD', 'STRIKER', 'LW', 'RW', 'CF', 'ST', 'FW',
    'UNASSIGNED'
  ];
  
  // Custom sort function for players by position
  const sortPlayersByPosition = (a, b) => {
    const positionA = (a.position || '').toUpperCase();
    const positionB = (b.position || '').toUpperCase();
    
    // Handle null/empty positions - they go to the end
    if (!positionA && !positionB) {
      // Both have no position - sort by name
      const nameA = `${a.firstName} ${a.lastName}`;
      const nameB = `${b.firstName} ${b.lastName}`;
      return nameA.localeCompare(nameB);
    }
    if (!positionA) return 1; // A goes to end
    if (!positionB) return -1; // B goes to end
    
    const indexA = positionOrder.indexOf(positionA);
    const indexB = positionOrder.indexOf(positionB);
    
    // If position not found in order, put at end (but before null positions)
    const finalIndexA = indexA === -1 ? positionOrder.length - 1 : indexA;
    const finalIndexB = indexB === -1 ? positionOrder.length - 1 : indexB;
    
    // If same position, sort by name
    if (finalIndexA === finalIndexB) {
      const nameA = `${a.firstName} ${a.lastName}`;
      const nameB = `${b.firstName} ${b.lastName}`;
      return nameA.localeCompare(nameB);
    }
    
    return finalIndexA - finalIndexB;
  };
  
  // Sort players by position
  const sortedPlayers = allPlayers.sort(sortPlayersByPosition);
  
  // Separate managers for the manager section display
  const managers = allPlayers.filter(player => 
    player.role === 'Manager' || player.role === 'team-manager'
  );

  // Apply max players limit if specified (to sorted list)
  const displayPlayers = maxPlayers ? sortedPlayers.slice(0, maxPlayers) : sortedPlayers;

  // Use private style for both variants for consistency
  return (
    <Box sx={sx}>
      {/* Display Managers First */}
      {managers && managers.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold' }}>
            Manager{managers.length > 1 ? 's' : ''}
          </Typography>
          <Box sx={{ 
            border: '1px solid rgba(0,0,0,0.1)', 
            borderRadius: 1, 
            p: 1,
            backgroundColor: 'rgba(0,100,0,0.05)' // Slight green tint for managers
          }}>
            {managers.map((manager, index) => (
              <Box key={manager.id || `manager-${index}`} sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                py: 0.25,
                '&:not(:last-child)': {
                  borderBottom: '1px solid rgba(0,0,0,0.05)'
                }
              }}>
                {isAuthenticated && manager.userTenantId ? (
                  <Typography 
                    component={Link}
                    to={`/player/${manager.userTenantId}`}
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.75rem', 
                      fontWeight: 'bold',
                      textDecoration: 'none',
                      color: 'primary.main',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {manager.firstName} {manager.lastName}
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                    {manager.firstName} {manager.lastName}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {showHeader && (
        <Typography variant="subtitle2" className="team-roster-header" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <PlayersIcon fontSize="small" />
          Players
        </Typography>
      )}
      
      {allPlayers && allPlayers.length > 0 ? (
        <Box sx={{ 
          border: '1px solid rgba(0,0,0,0.1)', 
          borderRadius: 1, 
          p: 1,
          backgroundColor: 'rgba(0,0,0,0.02)'
        }}>
          {displayPlayers.map((player, index) => {
            // Format position display - don't show anything if position is null/empty
            const positionDisplay = player.position ? player.position.toUpperCase() : '';
            const jerseyDisplay = formatJerseyNumber(player.jerseyNumber);
            
            // Don't show jersey numbers for goalkeepers
            const isGoalkeeper = positionDisplay && ['GOALKEEPER', 'GK', 'GOALIE','K'].includes(positionDisplay);
            const showJerseyNumber = jerseyDisplay && !isGoalkeeper;
            
            return (
              <Box key={player.id || index} sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                py: 0.25,
                '&:not(:last-child)': {
                  borderBottom: '1px solid rgba(0,0,0,0.05)'
                }
              }}>
                {isAuthenticated && player.userTenantId ? (
                  <Typography 
                    component={Link}
                    to={`/player/${player.userTenantId}`}
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.75rem', 
                      flex: 1,
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover': {
                        color: 'primary.main',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {positionDisplay && (
                      <span style={{ fontWeight: 'bold', marginRight: '8px' }}>
                        {positionDisplay} -
                      </span>
                    )}
                    {!positionDisplay && <span style={{ marginRight: '0px' }}></span>}
                    {positionDisplay ? ' ' : ''}{player.firstName} {player.lastName}
                    {player.playerStatus === 'Injured' && (
                      <Typography component="span" variant="caption" color="error" sx={{ ml: 0.5 }}>
                        (Injured)
                      </Typography>
                    )}
                    {player.membershipType === 'LongTermSpare' && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        (Spare)
                      </Typography>
                    )}
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', flex: 1 }}>
                    {positionDisplay && (
                      <span style={{ fontWeight: 'bold', marginRight: '8px' }}>
                        {positionDisplay} -
                      </span>
                    )}
                    {!positionDisplay && <span style={{ marginRight: '0px' }}></span>}
                    {positionDisplay ? ' ' : ''}{player.firstName} {player.lastName}
                    {player.playerStatus === 'Injured' && (
                      <Typography component="span" variant="caption" color="error" sx={{ ml: 0.5 }}>
                        (Injured)
                      </Typography>
                    )}
                    {player.membershipType === 'LongTermSpare' && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        (Spare)
                      </Typography>
                    )}
                  </Typography>
                )}
                {showJerseyNumber && (
                  <Typography variant="caption" sx={{ 
                    fontWeight: 'bold', 
                    fontSize: '0.75rem',
                    color: 'primary.main',
                    marginLeft: '8px'
                  }}>
                    #{jerseyDisplay}
                  </Typography>
                )}
              </Box>
            );
          })}
          {maxPlayers && allPlayers.length > maxPlayers && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              py: 0.25,
              borderTop: '1px solid rgba(0,0,0,0.05)'
            }}>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', fontStyle: 'italic', color: 'text.secondary' }}>
                ... and {allPlayers.length - maxPlayers} more
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No players registered
        </Typography>
      )}
    </Box>
  );
};

export default TeamRoster;